/**
 * Iter129 — Personal notification preferences UI.
 *
 * Backed by:
 *   GET  /api/me/notification-prefs
 *   PATCH /api/me/notification-prefs
 *
 * Renders a table of every event × channel checkboxes so any user
 * (staff or client) can silence per-event, per-channel notifications
 * for themselves without needing an admin to change the company-wide
 * settings.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { Save, RefreshCw, Lock } from 'lucide-react';

const CHANNELS = [
  { key: 'in_app', label: 'In-App' },
  { key: 'email',  label: 'Email' },
  { key: 'push',   label: 'Push' },
];

export default function NotificationPreferencesTab() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [events, setEvents] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Reuse company endpoint just to get all_events list; user prefs come
      // from /me/notification-prefs
      const [permsR, prefsR] = await Promise.all([
        fetch(`${BACKEND_URL}/api/main-hub/notification-permissions`, { headers: H }),
        fetch(`${BACKEND_URL}/api/users/me/notification-prefs`, { headers: H }),
      ]);
      if (permsR.ok) {
        const d = await permsR.json();
        setEvents(d.all_events || []);
      }
      if (prefsR.ok) {
        const d = await prefsR.json();
        setPrefs(d.overrides || {});
        // Fall back to the endpoint's own event list if company perms fetch failed
        if ((!permsR.ok || (await permsR).status !== 200) && d.all_events) {
          setEvents(d.all_events);
        }
      }
    } catch (e) {
      // silent
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (evt, ch, on) => {
    setPrefs(prev => {
      const next = { ...prev };
      const cur = next[evt] || { in_app: true, email: true, push: true };
      next[evt] = { ...cur, [ch]: on };
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/users/me/notification-prefs`, {
        method: 'PATCH', headers: JH,
        body: JSON.stringify({ overrides: prefs }),
      });
      if (r.ok) toast({ title: 'Preferences saved' });
      else toast({ title: 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filt = q ? events.filter(e => e.event.toLowerCase().includes(q) || (e.module || '').toLowerCase().includes(q)) : events;
    const out = {};
    for (const e of filt) {
      const m = e.module || 'system';
      (out[m] = out[m] || []).push(e);
    }
    return out;
  }, [events, search]);

  return (
    <div className="space-y-4" data-testid="notification-prefs-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search events / modules…"
          className="bg-slate-800 border-slate-600 text-white max-w-xs" data-testid="my-notifs-search" />
        <Button variant="ghost" onClick={load} className="text-slate-300" data-testid="my-notifs-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold ml-auto" data-testid="my-notifs-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([mod, list]) => (
          <div key={mod} className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-800/60 px-3 py-2 text-xs font-mono uppercase text-cyan-300">{mod}</div>
            <div className="divide-y divide-slate-800">
              {list.map(e => {
                const p = prefs[e.event] || { in_app: true, email: true, push: true };
                return (
                  <div key={e.event} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="min-w-0 flex-1 mr-3">
                      <div className="text-slate-100 font-mono text-xs">{e.event}</div>
                      {e.critical && (
                        <div className="text-[10px] text-red-400 mt-0.5 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> critical — cannot be silenced
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {CHANNELS.map(c => (
                        <label key={c.key} className="flex items-center gap-1 text-[11px] text-slate-300">
                          <Switch
                            checked={!!p[c.key]}
                            onCheckedChange={v => toggle(e.event, c.key, v)}
                            disabled={e.critical}
                            data-testid={`pref-${e.event}-${c.key}`}
                          />
                          <span>{c.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
